import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Space, Spin } from "antd";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CardAttachModals, CardAttachModalsType } from "entities/card";
import { getProfile } from "entities/viewer";
import { useRef } from "react";
import { notification } from "shared/lib/antd-global";
import { getTransactionsForRefund, transactionsPayout } from "../api";
import styles from "./index.module.scss";

export const ReturnTransactionsBlock = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showInfoOrganizationModalRef = useRef<CardAttachModalsType>(null);

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["return-transactions"],
    queryFn: () => getTransactionsForRefund(),
    staleTime: 0,
  });

  const { mutate: getPayout, isPending } = useMutation({
    mutationFn: ({ id, cardId }: { id: string; cardId?: string }) =>
      transactionsPayout(id, cardId ? { card_id: cardId } : undefined),
    onSuccess(response) {
      if (response.success) {
        if (response.form_url) {
          window.open(response.form_url, "_blank");
        } else {
          notification.success({ title: t("payment_details.payout_success") });
        }
      } else {
        notification.error({ title: t("payment_details.payment_not_found") });
      }
      showInfoOrganizationModalRef.current?.close();
      queryClient.invalidateQueries({ queryKey: ["return-transactions"] });
    },
    onError(error: AxiosError<{ message: string }>) {
      showInfoOrganizationModalRef.current?.close();
      notification.error({
        title:
          error.response?.data?.message ||
          t("payment_details.payment_not_found"),
      });
      queryClient.invalidateQueries({ queryKey: ["return-transactions"] });
    },
  });

  const handelReceive = () => {
    if (profile?.features?.wooppay_card_payouts) {
      if (localStorage.getItem("showCardPayoutsAlert")) {
        showInfoOrganizationModalRef.current?.open();
      } else {
        showInfoOrganizationModalRef.current?.openAlertModal();
      }
    } else {
      getPayout({ id: data?.data[0].id || "" });
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 80,
        }}
      >
        <Spin />
      </div>
    );
  }

  if (!data?.data.length) {
    return null;
  }

  return (
    <>
      <div className={styles.block}>
        <div className={styles.iconWrapper}>
          <ExclamationCircleOutlined />
        </div>
        <Space vertical size="middle">
          {t("refund.refund_available")}
          <div className={styles.text}>
            {t("refund.refund_text", { amount: data.data[0].amount })}
          </div>
          <Button
            type="primary"
            onClick={handelReceive}
            loading={isPending}
            disabled={isPending}
          >
            {t("refund.refund_button", { amount: data.data[0].amount })}
          </Button>
        </Space>
      </div>

      <CardAttachModals
        ref={showInfoOrganizationModalRef}
        id={data.data[0].id}
        isPending={isPending}
        getPayout={getPayout}
      />
    </>
  );
};
